import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { listDailyWorkDaysForSubs } from "./daily-work.api";
import { DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { ReadAllResponse } from "@/interfaces";

interface FilterState {
  startDate: string;
  endDate: string;
  search: string;
  status: string;
  sortKey: string;
  sortOrder: "asc" | "desc";
}

interface PaginationState {
  page: number;
  pageSize: number;
}

interface SubsDailyWorkState {
  list: DailyWorkDayJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
}

const initialState: SubsDailyWorkState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    startDate: "",
    endDate: "",
    search: "",
    status: "",
    sortKey: "work_date",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 10 },
};

export const fetchSubsDailyWorkDays = createAsyncThunk("subsDailyWork/fetch", async (_, { getState }) => {
  const s = (getState() as { subsDailyWork: SubsDailyWorkState }).subsDailyWork;
  const { page, pageSize } = s.pagination;
  const { startDate, endDate, search, sortKey, sortOrder } = s.filters;

  return await listDailyWorkDaysForSubs(
    page,
    pageSize,
    {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      search: search || undefined,
      status: s.filters.status || undefined,
    },
    sortKey,
    sortOrder
  );
});

const slice = createSlice({
  name: "subsDailyWork",
  initialState,
  reducers: {
    setStartDate(s, a: PayloadAction<string>) {
      s.filters.startDate = a.payload;
      s.pagination.page = 1;
    },
    setEndDate(s, a: PayloadAction<string>) {
      s.filters.endDate = a.payload;
      s.pagination.page = 1;
    },
    setSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1;
    },
    setStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1;
    },
    setPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
    },
    setSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchSubsDailyWorkDays.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchSubsDailyWorkDays.fulfilled, (s, a: PayloadAction<ReadAllResponse<DailyWorkDayJoin>>) => {
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchSubsDailyWorkDays.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message || "Failed to fetch daily work days.";
    });
  },
});

export const { setStartDate, setEndDate, setSearch, setStatus, setPage, setPageSize, setSortKey } = slice.actions;

export default slice.reducer;

